// Changing one of Bailey's plans by asking (2026-10-06): "drop the text",
// "move the Story to Thursday evening", "make the email less formal". Bailey
// answers with a plans block naming the plan and its edits; they're applied
// here, kept per plan (so the card, Marketing home's studio hand-over and the
// next edit all start from the changed plan), and never send anything:
// approving is still "Review and approve" in the campaign studio.
import moment from 'moment'

const KEY = () => { let s = ''; try { s = localStorage.getItem('salonId') || '' } catch (e) { /* private mode */ } return `beu-bailey-plan-edits-${s}` }
const readAll = () => { try { return JSON.parse(localStorage.getItem(KEY())) || {} } catch (e) { return {} } }
const writeAll = (all) => { try { localStorage.setItem(KEY(), JSON.stringify(all)) } catch (e) { /* private mode */ } }

/** Every edit made to this plan so far, oldest first. */
export const planEditsFor = (key) => (key && readAll()[key]) || []

/** Add edits to a plan (once per chat block: `blockId`). */
export const addPlanEdits = (key, edits, blockId) => {
  if (!key || !Array.isArray(edits) || !edits.length) return
  const all = readAll()
  const list = all[key] || []
  if (blockId && list.some(e => e.from === blockId)) return
  all[key] = [...list, ...edits.map(e => ({ ...e, from: blockId || null }))].slice(-60)
  // Keep it small: the 30 most recently changed plans
  const keys = Object.keys(all)
  if (keys.length > 30) delete all[keys[0]]
  writeAll(all)
}

const setPath = (obj, path, value) => {
  const [head, ...rest] = path
  return rest.length ? { ...obj, [head]: setPath((obj && obj[head]) || {}, rest, value) } : { ...obj, [head]: value }
}

/** The plan with these edits applied. Unknown items and fields are ignored. */
export const applyPlanEdits = (plan, edits = []) => {
  if (!plan || !Array.isArray(plan.items) || !edits.length) return plan
  let items = plan.items
  edits.forEach((e) => {
    items = items.map((it) => {
      if (it.id !== e.item) return it
      let next = it
      if (typeof e.on === 'boolean') next = { ...next, on: e.on }
      if (e.at) {
        const m = moment(e.at, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD'], true)
        if (m.isValid()) {
          const at = e.at.length === 10 ? moment(m).hours(moment(it.at).hours()).minutes(moment(it.at).minutes()) : m
          next = { ...next, at: at.toISOString() }
          if (next.post && next.post.plannedAt) next = { ...next, post: { ...next.post, plannedAt: at.toISOString() } }
        }
      }
      if (next.channel === 'sms' && e.message) next = { ...next, message: e.message }
      if (next.channel === 'email' && next.email) {
        if (e.subject) next = setPath(next, ['email', 'subject'], e.subject)
        if (e.heading) next = setPath(next, ['email', 'hero', 'headline'], e.heading)
        if (e.body) next = setPath(next, ['email', 'hero', 'body'], e.body)
      }
      if (next.post) {
        if (e.headline) next = setPath(next, ['post', 'content', 'headline'], e.headline)
        if (e.cta) next = setPath(next, ['post', 'content', 'cta'], e.cta)
      }
      return next
    })
  })
  const ats = items.map(i => moment(i.at)).sort((a, b) => a - b)
  return { ...plan, items, ...(ats.length ? { start: ats[0].format('YYYY-MM-DD'), end: ats[ats.length - 1].format('YYYY-MM-DD') } : {}), edited: true }
}

/** The plan as it stands now (its saved edits applied). */
export const currentPlan = (plan) => applyPlanEdits(plan, planEditsFor(plan && plan.key))

const NAMES = { sms: 'text', email: 'email', post: 'post', story: 'Story' }
const at = (iso) => moment(iso).format('ddd D MMM HH:mm')

/** What's on screen, for Bailey: each plan's key and items, with their words. */
export const planFacts = (plans = []) => {
  if (!plans.length) return []
  const lines = ['PLANS ON SCREEN (to change one, answer with a plans block: {"show": "plans", "plan": "<key>", "edits": [{"item": "<id>", ...}]}; per item: "on" true/false, "at" "YYYY-MM-DDTHH:mm", for the text "message", for the email "subject" / "heading" / "body", for a post or Story "headline" / "cta". Only change what they asked; keep {{FirstName}}, {{SalonName}} and {{BookingLink}}):']
  plans.forEach((p) => {
    lines.push(`- "${p.title}" (key ${p.key}):`)
    p.items.forEach((it) => {
      const words = it.channel === 'sms' ? ` "${String(it.message || '').slice(0, 200)}"`
        : it.channel === 'email' && it.email ? ` subject "${it.email.subject || ''}", heading "${(it.email.hero && it.email.hero.headline) || ''}"`
          : it.post && it.post.content && it.post.content.headline ? ` headline "${it.post.content.headline}"` : ''
      lines.push(`  - ${it.id}: ${NAMES[it.channel] || it.channel}${it.post ? ` (${it.post.type})` : ''}, ${at(it.at)}, ${it.on ? 'on' : 'off'}${words}`)
    })
  })
  return lines
}

/** In words, what a set of edits did (for the card and for Bailey). */
export const editsSaid = (plan, edits = []) => edits.map((e) => {
  const it = (plan.items || []).find(x => x.id === e.item)
  if (!it) return null
  const name = NAMES[it.channel] || it.channel
  if (e.on === false) return `${name} switched off`
  if (e.on === true) return `${name} switched on`
  if (e.at) return `${name} moved to ${moment(e.at).format('ddd D MMM h:mma').replace(':00', '')}`
  return `${name} reworded`
}).filter(Boolean)
